/**
 * @element qds-powerbi-embed
 * 
 * A generic Power BI report embedding component for displaying Power BI reports and dashboards.
 * 
 * @attr {string} workspace-id - Power BI workspace ID
 * @attr {string} report-id - Power BI report ID
 * @attr {string} page-name - Specific page to display (optional)
 * @attr {string} embed-mode - Embed mode: view, edit, create (default: view)
 * @attr {boolean} show-filter-pane - Show/hide filter pane (default: false)
 * @attr {boolean} show-nav-pane - Show/hide navigation pane (default: false)
 * @attr {string} filters - JSON string of pre-applied filters (optional)
 * @attr {string} auth-token - Power BI user access token (for API calls)
 * @attr {string} embed-token - Power BI embed token (for iframe authentication)
 * @attr {string} embed-url - Custom embed URL (optional, will be generated if not provided)
 * 
 * @csspart container - The main container element
 * @csspart iframe - The Power BI iframe element
 * @csspart loading - The loading state element
 * @csspart error - The error state element
 * 
 * @fires loaded - Fired when the Power BI report is loaded
 * @fires error - Fired when an error occurs during embedding
 * @fires dataSelected - Fired when data is selected in the report
 */

import { loadBaseCSS } from '../shared/button-utils.js';

// Power BI SDK interfaces
declare global {
  interface Window {
    powerbi: any;
    models: any;
  }
}

// Custom event interfaces
interface PowerBILoadedEventDetail {
  reportId: string;
  workspaceId: string;
}

interface PowerBIErrorEventDetail {
  error: string;
  reportId?: string;
}

interface PowerBIDataSelectedEventDetail {
  selection: any;
  reportId: string;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return typeof error === 'string' ? error : 'Unknown error';
}

// Load the base CSS
loadBaseCSS();

export class QdsPowerBIEmbed extends HTMLElement {
  // Private properties
  private _workspaceId: string = '';
  private _reportId: string = '';
  private _pageName: string = '';
  private _embedMode: string = 'view';
  private _showFilterPane: boolean = false;
  private _showNavPane: boolean = false;
  private _filters: string = '';
  private _authToken: string = '';
  private _embedToken: string = '';
  private _embedUrl: string = '';
  private _isLoaded: boolean = false;
  private _report: any = null;
  private _powerBILoaded: boolean = false;

  static get observedAttributes(): string[] {
    return [
      'workspace-id',
      'report-id', 
      'page-name',
      'embed-mode',
      'show-filter-pane',
      'show-nav-pane',
      'filters',
      'auth-token',
      'embed-token',
      'embed-url'
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.render();
    this._loadPowerBISDK();
  }

  // Lifecycle callbacks
  connectedCallback(): void {
    if (!this.isConnected) return;
    
    // Set default attributes if not already set
    if (!this.hasAttribute('embed-mode')) {
      this.setAttribute('embed-mode', 'view');
    }
    if (!this.hasAttribute('show-filter-pane')) {
      this.setAttribute('show-filter-pane', 'false');
    }
    if (!this.hasAttribute('show-nav-pane')) {
      this.setAttribute('show-nav-pane', 'false');
    }

    this._setupEventListeners();
  }

  disconnectedCallback(): void {
    this._removeEventListeners();
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) return;
    
    console.log(`Attribute changed: ${name} = ${newValue}`);

    switch (name) {
      case 'workspace-id':
        this._workspaceId = newValue || '';
        console.log(`Workspace ID updated to: ${this._workspaceId}`);
        break;
      case 'report-id':
        this._reportId = newValue || '';
        console.log(`Report ID updated to: ${this._reportId}`);
        break;
      case 'page-name':
        this._pageName = newValue || '';
        break;
      case 'embed-mode':
        this._embedMode = newValue || 'view';
        break;
      case 'show-filter-pane':
        this._showFilterPane = newValue === 'true';
        break;
      case 'show-nav-pane':
        this._showNavPane = newValue === 'true';
        break;
      case 'filters':
        this._filters = newValue || '';
        break;
      case 'auth-token':
        this._authToken = newValue || '';
        break;
      case 'embed-token':
        this._embedToken = newValue || '';
        console.log('Embed token set, re-embedding report...');
        break;
      case 'embed-url':
        this._embedUrl = newValue || '';
        break;
    }

    // Re-render if component is connected and we have required attributes
    if (this.isConnected && this._canEmbed()) {
      this._embedReport();
    }
  }

  // Public methods
  public async refresh(): Promise<void> {
    if (this._report && this._isLoaded) {
      try {
        await this._report.refresh();
        console.log('Report refreshed successfully');
      } catch (error) {
        console.error('Error refreshing report:', error);
        // Fallback to re-embedding
        this._embedReport();
      }
    } else {
      // Re-embed if no report is loaded
      this._embedReport();
    }
  }

  public async exportToPDF(): Promise<void> {
    if (this._report && this._isLoaded) {
      try {
        // Use fallback export data type if models not available
        const exportDataType = window.models?.ExportDataType?.Summarized || 0;
        const exportData = await this._report.exportData(exportDataType);
        console.log('PDF export requested for report:', this._reportId);
        this._fireEvent('export-requested', { format: 'pdf', reportId: this._reportId, data: exportData });
      } catch (error) {
        console.error('Error exporting report:', error);
        this._fireEvent('export-requested', {
          format: 'pdf',
          reportId: this._reportId,
          error: getErrorMessage(error)
        });
      }
    }
  }

  // Generate embed token using user access token
  public async generateEmbedToken(): Promise<string | null> {
    // Debug: log current state
    console.log('generateEmbedToken called with:', {
      hasAuthToken: !!this._authToken,
      authTokenLength: this._authToken?.length,
      workspaceId: this._workspaceId,
      reportId: this._reportId,
      pageName: this._pageName
    });
    
    if (!this._authToken || !this._workspaceId || !this._reportId) {
      console.error('Missing required parameters for embed token generation:', {
        authToken: !!this._authToken,
        workspaceId: !!this._workspaceId,
        reportId: !!this._reportId
      });
      return null;
    }

    try {
      const embedTokenUrl = `https://api.powerbi.com/v1.0/myorg/groups/${this._workspaceId}/reports/${this._reportId}/GenerateToken`;
      
      const requestBody = {
        accessLevel: 'View',
        allowSaveAs: false
      };

      console.log('Making embed token request:', {
        url: embedTokenUrl,
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this._authToken.substring(0, 20)}...`,
          'Content-Type': 'application/json'
        },
        body: requestBody
      });

      const response = await fetch(embedTokenUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this._authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      console.log('API Response:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`Failed to generate embed token: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      return data.token;
    } catch (error) {
      console.error('Error generating embed token:', error);
      this._fireEvent('error', {
        error: 'Failed to generate embed token',
        details: getErrorMessage(error)
      });
      return null;
    }
  }

  // Debug method to check internal state
  public debugState(): any {
    return {
      workspaceId: this._workspaceId,
      reportId: this._reportId,
      pageName: this._pageName,
      authToken: this._authToken ? `${this._authToken.substring(0, 20)}...` : null,
      embedToken: this._embedToken ? `${this._embedToken.substring(0, 20)}...` : null,
      embedMode: this._embedMode,
      showFilterPane: this._showFilterPane,
      showNavPane: this._showNavPane
    };
  }

  // Private methods
  private _canEmbed(): boolean {
    const canEmbed = !!(this._workspaceId && this._reportId && this._powerBILoaded);
    console.log(`Can embed check: workspaceId=${this._workspaceId}, reportId=${this._reportId}, powerBILoaded=${this._powerBILoaded}, canEmbed=${canEmbed}`);
    return canEmbed;
  }

  private async _loadPowerBISDK(): Promise<void> {
    // Check if Power BI SDK is already loaded
    if (window.powerbi && window.models) {
      this._powerBILoaded = true;
      console.log('Power BI SDK already loaded');
      return;
    }

    console.log('Loading Power BI SDK from CDN...');
    
    // Load Power BI SDK
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/powerbi-client@2.23.1/dist/powerbi.min.js';
    script.crossOrigin = 'anonymous';
    
    await new Promise<void>((resolve, reject) => {
      script.onload = () => {
        console.log('Script loaded, checking for Power BI objects...');
        // Give it a moment to initialize
        setTimeout(() => {
          console.log('window.powerbi:', !!window.powerbi);
          console.log('window.models:', !!window.models);
          console.log('window["powerbi-client"]:', !!(window as any)['powerbi-client']);
          console.log('Available window keys:', Object.keys(window).filter(k => k.toLowerCase().includes('power')));
          
          // Check if powerbi-client exists and extract powerbi and models from it
          const powerbiclient = (window as any)['powerbi-client'];
          if (powerbiclient) {
            console.log('Found powerbi-client object:', Object.keys(powerbiclient));
            
            // The powerbi-client module structure - let's explore it
            console.log('powerbiclient.service:', !!powerbiclient.service);
            console.log('powerbiclient.models:', !!powerbiclient.models);
            console.log('powerbiclient.embed:', !!powerbiclient.embed);
            console.log('powerbiclient.powerbi:', !!powerbiclient.powerbi);
            
            // Try different ways to access the embed service
            if (powerbiclient.embed) {
              window.powerbi = powerbiclient;
            } else if (powerbiclient.service) {
              window.powerbi = powerbiclient.service;
            } else {
              window.powerbi = powerbiclient;
            }
            
            if (powerbiclient.models) {
              window.models = powerbiclient.models;
            }
            
            console.log('Assigned window.powerbi:', !!window.powerbi);
            console.log('window.powerbi.embed:', !!window.powerbi?.embed);
            console.log('window.powerbi methods:', window.powerbi ? Object.getOwnPropertyNames(window.powerbi) : 'none');
          }
          
          if (window.powerbi && window.models) {
            this._powerBILoaded = true;
            console.log('Power BI SDK loaded successfully');
            console.log('Available models:', Object.keys(window.models));
            resolve();
          } else {
            console.error('Power BI SDK script loaded but powerbi/models not found');
            console.log('powerbiclient structure:', powerbiclient ? Object.keys(powerbiclient) : 'not found');
            reject(new Error('Power BI objects not found after script load'));
          }
        }, 500);
      };
      
      script.onerror = (error) => {
        console.error('Script loading error:', error);
        reject(new Error('Failed to load Power BI SDK script'));
      };
      
      document.head.appendChild(script);
    });

    // Try to embed if we have the required attributes
    if (this.isConnected && this._canEmbed()) {
      this._embedReport();
    }
  }

  private _generateEmbedUrl(): string {
    if (this._embedUrl) {
      return this._embedUrl;
    }

    // Generate Power BI embed URL
    let url = `https://app.powerbi.com/reportEmbed?reportId=${this._reportId}&groupId=${this._workspaceId}`;
    
    if (this._pageName) {
      url += `&pageName=${encodeURIComponent(this._pageName)}`;
    }

    // Add embed token if available (required for authenticated private reports)
    if (this._embedToken) {
      url += `&accessToken=${encodeURIComponent(this._embedToken)}`;
    }

    // Add debugging parameters
    url += `&config=dev&embed=true`;

    // Add configuration parameters
    const config = {
      filterPaneEnabled: this._showFilterPane,
      navContentPaneEnabled: this._showNavPane,
      bars: {
        statusBar: { visible: false },
        actionBar: { visible: true }
      }
    };

    console.log('Embed config object:', config);
    console.log('Embed config JSON:', JSON.stringify(config));
    console.log('Embed config URL encoded:', encodeURIComponent(JSON.stringify(config)));

    url += `&config=${encodeURIComponent(JSON.stringify(config))}`;

    return url;
  }

  private _embedReport(): void {
    const container = this.shadowRoot?.querySelector('.powerbi-container');
    if (!container || !this._canEmbed()) {
      this._showError('Missing required attributes: workspace-id, report-id, or Power BI SDK not loaded');
      return;
    }

    // Show loading state
    this._showLoading();

    try {
      // Check that we have required data
      if (!this._workspaceId || !this._reportId) {
        throw new Error(`Missing required data: workspaceId=${this._workspaceId}, reportId=${this._reportId}`);
      }

      // Generate embed URL
      const embedUrl = this._generateEmbedUrl();
      console.log('Generated embed URL:', embedUrl);
      
      if (!embedUrl) {
        throw new Error('Failed to generate embed URL');
      }

      // Check for access token
      const accessToken = this._embedToken || this._authToken;
      if (!accessToken) {
        this._showError('Authentication required: Please provide an embed token or user access token');
        this._fireEvent('error', {
          error: 'Authentication required',
          reportId: this._reportId,
          hasAuthToken: false,
          hasEmbedToken: false
        });
        return;
      }

      // Determine token type with fallback
      let tokenType = 0; // Default to Aad (0)
      if (window.models && window.models.TokenType) {
        tokenType = this._embedToken ? window.models.TokenType.Embed : window.models.TokenType.Aad;
      } else {
        // Fallback values based on Power BI SDK documentation
        tokenType = this._embedToken ? 1 : 0; // Embed = 1, Aad = 0
        console.warn('Using fallback token types - Power BI models not fully loaded');
      }

      // Create embed configuration using Power BI SDK format
      const embedConfig = {
        type: 'report',
        id: this._reportId,
        embedUrl: embedUrl,
        accessToken: accessToken,
        tokenType: tokenType,
        settings: {
          filterPaneEnabled: this._showFilterPane,
          navContentPaneEnabled: this._showNavPane,
          bars: {
            statusBar: {
              visible: false
            },
            actionBar: {
              visible: true
            }
          }
        }
      };

      console.log('Embed config object:', embedConfig);
      console.log('Embed config validation:', {
        hasType: !!embedConfig.type,
        hasId: !!embedConfig.id,
        hasEmbedUrl: !!embedConfig.embedUrl,
        hasAccessToken: !!embedConfig.accessToken,
        embedUrlLength: embedConfig.embedUrl?.length,
        accessTokenLength: embedConfig.accessToken?.length
      });

      // Clear any existing report
      if (this._report) {
        try {
          if (window.powerbi.reset) {
            window.powerbi.reset(container);
          }
        } catch (resetError) {
          console.warn('Error resetting existing report:', resetError);
        }
      }

      // Use the Power BI SDK to create the service and embed the report
      const powerbiclient = (window as any)['powerbi-client'];
      
      console.log('Available powerbi-client classes:', Object.keys(powerbiclient));
      
      // Create a PowerBI service instance
      let powerbiService;
      if (powerbiclient.service) {
        powerbiService = powerbiclient.service;
      } else if (powerbiclient.PowerBIService) {
        powerbiService = new powerbiclient.PowerBIService();
      } else if (powerbiclient.Service) {
        powerbiService = new powerbiclient.Service();
      } else {
        // Create a basic service object
        powerbiService = {
          embed: (container: any, config: any) => {
            return new powerbiclient.Report(container, config);
          }
        };
      }
      
      console.log('PowerBI service:', powerbiService);
      console.log('Service embed method:', typeof powerbiService.embed);
      
      // Use the service to embed the report
      if (powerbiService.embed && typeof powerbiService.embed === 'function') {
        this._report = powerbiService.embed(container, embedConfig);
        console.log('Report embedded using service.embed');
      } else {
        // Fallback: try to create Report directly
        this._report = new powerbiclient.Report(container, embedConfig);
        console.log('Report created directly using Report constructor');
      }

      // Set up event handlers
      this._report.on('loaded', () => {
        console.log('Power BI report loaded successfully');
        this._isLoaded = true;
        this._hideLoading();
        this._fireEvent('loaded', {
          reportId: this._reportId,
          workspaceId: this._workspaceId,
          hasAuthToken: !!this._authToken,
          hasEmbedToken: !!this._embedToken
        });
      });

      this._report.on('error', (event: any) => {
        console.error('Power BI report error:', event.detail);
        this._showError(`Report error: ${event.detail.message || 'Unknown error'}`);
        this._fireEvent('error', {
          error: event.detail.message || 'Unknown error',
          reportId: this._reportId,
          hasAuthToken: !!this._authToken,
          hasEmbedToken: !!this._embedToken,
          detail: event.detail
        });
      });

      this._report.on('dataSelected', (event: any) => {
        console.log('Data selected in report:', event.detail);
        this._fireEvent('dataSelected', {
          selection: event.detail,
          reportId: this._reportId
        });
      });

      // Add timeout for cases where report doesn't load
      const loadTimeout = setTimeout(() => {
        if (!this._isLoaded) {
          if (!this._embedToken && !this._authToken) {
            this._showError('Authentication required: Please provide an embed token or user access token');
          } else if (!this._embedToken && this._authToken) {
            this._showError('Embed token required: Use generateEmbedToken() method or provide embed-token attribute');
          } else {
            this._showError('Report loading timeout: Check your permissions and network connection');
          }
          this._fireEvent('error', {
            error: 'Loading timeout',
            reportId: this._reportId,
            hasAuthToken: !!this._authToken,
            hasEmbedToken: !!this._embedToken
          });
        }
      }, 15000); // 15 second timeout for SDK loading

      // Clear timeout on successful load
      this._report.on('loaded', () => {
        clearTimeout(loadTimeout);
      });

    } catch (error) {
      console.error('Error embedding Power BI report:', error);
      const errorMessage = getErrorMessage(error);
      this._showError(`Failed to embed report: ${errorMessage}`);
      this._fireEvent('error', {
        error: `Failed to embed report: ${errorMessage}`,
        reportId: this._reportId,
        hasAuthToken: !!this._authToken,
        hasEmbedToken: !!this._embedToken
      });
    }
  }


  private _showLoading(): void {
    const loading = this.shadowRoot?.querySelector('.loading-state');
    const error = this.shadowRoot?.querySelector('.error-state');
    
    if (loading) loading.classList.remove('hidden');
    if (error) error.classList.add('hidden');
  }

  private _hideLoading(): void {
    const loading = this.shadowRoot?.querySelector('.loading-state');
    if (loading) {
      loading.classList.add('hidden');
      console.log('Loading state hidden, report should now be visible');
    }
  }

  private _showError(message: string): void {
    const error = this.shadowRoot?.querySelector('.error-state');
    const loading = this.shadowRoot?.querySelector('.loading-state');
    const errorText = this.shadowRoot?.querySelector('.error-text');
    
    if (loading) loading.classList.add('hidden');
    if (error) error.classList.remove('hidden');
    if (errorText) errorText.textContent = message;
  }

  private _setupEventListeners(): void {
    // Add any global event listeners here
  }

  private _removeEventListeners(): void {
    // Remove any global event listeners here
  }

  private _fireEvent(eventName: string, detail: any): void {
    const event = new CustomEvent(eventName, {
      detail,
      bubbles: true,
      composed: true
    });
    this.dispatchEvent(event);
  }

  private render(): void {
    const styles = `
      <style>
        :host {
          --_powerbi-embed-surface: var(--powerbi-embed-background, var(--theme-background-primary));
          --_powerbi-embed-overlay: var(--powerbi-embed-overlay-background, var(--theme-background-overlay, var(--theme-background-primary)));
          --_powerbi-embed-border: var(--powerbi-embed-border-color, var(--theme-border-primary));
          --_powerbi-embed-text: var(--powerbi-embed-text-color, var(--theme-text-primary));
          --_powerbi-embed-subtle-text: var(--powerbi-embed-subtle-text-color, var(--theme-text-secondary));
          --_powerbi-embed-accent: var(--powerbi-embed-accent-color, var(--theme-accent-primary));
          --_powerbi-embed-error: var(--powerbi-embed-error-color, var(--theme-error));
          --_powerbi-embed-radius: var(--powerbi-embed-radius, var(--theme-radius-md, 8px));
          --_powerbi-embed-spinner-size: var(--powerbi-embed-spinner-size, 40px);
          --_powerbi-embed-min-height: var(--powerbi-embed-min-height, 600px);
          --_powerbi-embed-mobile-min-height: var(--powerbi-embed-mobile-min-height, 400px);
          --_powerbi-embed-gap: var(--powerbi-embed-gap, var(--theme-space-4, 16px));
          display: block;
          width: 100%;
          height: var(--_powerbi-embed-min-height);
          min-height: var(--_powerbi-embed-min-height);
          position: relative;
          box-sizing: border-box;
          border: 1px solid var(--_powerbi-embed-border);
          border-radius: var(--_powerbi-embed-radius);
          overflow: hidden;
          background: var(--_powerbi-embed-surface);
          color: var(--_powerbi-embed-text);
          font-family: var(--theme-font-family, var(--qds-font-family, 'IBM Plex Sans', sans-serif));
        }

        .powerbi-container {
          width: 100%;
          height: 100%;
          position: relative;
        }

        .powerbi-container > * {
          width: 100%;
          height: 100%;
          border: none;
          display: block;
          background: var(--_powerbi-embed-surface);
          min-height: var(--_powerbi-embed-min-height);
        }

        .loading-state {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--_powerbi-embed-overlay);
          flex-direction: column;
          gap: var(--_powerbi-embed-gap);
        }

        .loading-spinner {
          width: var(--_powerbi-embed-spinner-size);
          height: var(--_powerbi-embed-spinner-size);
          border: 3px solid var(--_powerbi-embed-border);
          border-top: 3px solid var(--_powerbi-embed-accent);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .loading-text {
          color: var(--_powerbi-embed-subtle-text);
          font-size: var(--theme-font-size-sm, 14px);
        }

        .error-state {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--_powerbi-embed-overlay);
          flex-direction: column;
          gap: var(--_powerbi-embed-gap);
          padding: var(--theme-space-5, 20px);
          box-sizing: border-box;
        }

        .error-icon {
          color: var(--_powerbi-embed-error);
          font-size: calc(var(--theme-font-size-lg, 18px) * 1.35);
        }

        .error-text {
          color: var(--_powerbi-embed-text);
          font-size: var(--theme-font-size-sm, 14px);
          text-align: center;
          max-width: 300px;
          line-height: 1.5;
        }

        .hidden {
          display: none !important;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Responsive behavior */
        @media (max-width: 768px) {
          :host {
            height: var(--_powerbi-embed-mobile-min-height);
            min-height: var(--_powerbi-embed-mobile-min-height);
          }
        }
      </style>
    `;

    const template = `
      ${styles}
      <div class="powerbi-container" part="container">
        <div class="loading-state" part="loading">
          <div class="loading-spinner"></div>
          <div class="loading-text">Loading Power BI Report...</div>
        </div>
        <div class="error-state hidden" part="error">
          <div class="error-icon">⚠</div>
          <div class="error-text">Failed to load report</div>
        </div>
      </div>
    `;

    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = template;
    }

    // Attempt to embed if we have the required attributes
    if (this._canEmbed()) {
      this._embedReport();
    } else {
      this._showError('Missing required attributes: workspace-id and report-id are required');
    }
  }
}

// Register the custom element
customElements.define('qds-powerbi-embed', QdsPowerBIEmbed);

export default QdsPowerBIEmbed;
